import React, { Fragment, useState } from 'react';
import { useForm, Control, FieldValues } from 'react-hook-form';
import { Button, EmailValidator, FieldController, RequiredValidator, TextField, useMatchScreenWidth, ConfirmationPasswordValidator } from '@keplerco/core';
import accountStyles from '../../../account/account.module.css';
import classNames from 'classnames';
import { useAppActions } from '../../../../overmind';
import { CreateFreemiumAccountRequest } from '../../../../overmind/actions';
import { PagePath } from '../../../../navigation/navigation.enums';
import { FetchType } from '../../../../enums';
import { TertiaryButton } from '../../../../components/general/tertiary-button/tertiary-button';
import { PasswordStrength } from '../../../account/password-strength/password-strength';
import { themedAssetUrl } from '../../../../library/helpers/themed-asset-url';
import { AccountPageLoaderLayer } from '../../../../components/general/loading-state/loader-layers/account-page-loader-layer/account-page-loader-layer';
import { useNavigate } from 'react-router';

interface FormValues {
  email: string;
  companyName: string;
  password: string;
  confirmPassword: string;
}

export function FreemiumRegisterLayout(): JSX.Element {
  const navigate = useNavigate();
  const actions = useAppActions();
  const isMobile = useMatchScreenWidth('mobile');

  const [errorMessage, setErrorMessage] = useState<string>('');
  const { control, handleSubmit, getValues, watch } = useForm<FormValues>({ reValidateMode: 'onChange', mode: 'onBlur' });

  const [fieldValues, setFieldValues] = useState<{ [key: string]: any }>();

  // Watch form values to enable password strength meter
  watch(value => setFieldValues(value));

  const onSubmit = async (values: FormValues) => {
    setErrorMessage('');
    actions.startLoader({ path: PagePath.accountRegister, type: FetchType.PageFetching });
    try {
      // Create a UserRegistration object from the form values
      const registration: CreateFreemiumAccountRequest = {
        email: values.email,
        companyName: values.companyName,
        password: values.password,
        confirmPassword: values.confirmPassword
      };

      // Call the registerUser action
      const result = await actions.registerFreemiumAccount(registration);

      if (result && result.message) {
        // Navigate to email confirmation page
        navigate(`${PagePath.accountBase}${PagePath.accountVerifyEmail}?email=${values.email}`);
      } else {
        setErrorMessage('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrorMessage('An error occurred during registration. Please try again.');
    } finally {
      actions.stopLoader(PagePath.accountRegister);
    }
  };

  return (
    <div className={accountStyles.accountBackground} style={{ backgroundImage: `url(${themedAssetUrl('backgrounds/pattern.background.svg')})` }}>
      <div className={accountStyles.accountPage}>
        <div className={classNames('card', accountStyles.accountCard)}>
          <AccountPageLoaderLayer path={PagePath.accountRegister}>
            {
              !isMobile && (
                <div className={accountStyles.accountNavigationToggler}>
                  <span className="body">Already have an account?</span>
                  <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountLogin}`)}>
                    Sign in
                  </TertiaryButton>
                </div>
              )
            }

            <h1>Unlock Kepler's Powerful Talent Management Platform</h1>

            <div className={accountStyles.accountDivider}>Features You'll Unlock</div>

            <div style={{ textAlign: 'left', maxWidth: '550px', margin: '0 auto', padding: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="feature-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--primary)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>1</div>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>Tailored Skills Taxonomy</h3>
                    <p style={{ margin: 0, color: 'var(--accent-3)' }}>Get a custom skills taxonomy that perfectly matches your organization's unique structure and requirements.</p>
                  </div>
                </div>

                <div className="feature-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--primary)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>2</div>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>Organization-Specific Roles</h3>
                    <p style={{ margin: 0, color: 'var(--accent-3)' }}>Generate custom roles aligned with your company's structure rather than using generic templates.</p>
                  </div>
                </div>

                <div className="feature-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--primary)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>3</div>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>AI-Powered Customization</h3>
                    <p style={{ margin: 0, color: 'var(--accent-3)' }}>Our AI creates a role and skill taxonomy specifically tailored to your organization's unique needs.</p>
                  </div>
                </div>

                <div className="feature-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--primary)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>4</div>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>Complete Talent Framework</h3>
                    <p style={{ margin: 0, color: 'var(--accent-3)' }}>Build a comprehensive talent framework with roles and skills that reflect your organization's actual structure.</p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, maxWidth: '500px', margin: '0 auto', padding: '0 20px' }}>
              Create your free account to build custom roles and skills taxonomies that transform how you manage and develop talent.
            </div>

            <form
              id="freemiumRegistration"
              className={accountStyles.accountForm}
              autoComplete="off"
              onSubmit={handleSubmit(onSubmit)}
            >
              {errorMessage && (
                <div style={{ color: 'var(--r)', marginBottom: '10px', width: '100%', textAlign: 'center' }}>
                  {errorMessage}
                </div>
              )}

              <FieldController
                name="email"
                control={control as unknown as Control<FieldValues>}
                rules={new EmailValidator('Enter a valid email address')}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="email"
                    label="Work Email"
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter a valid email address',
                    }}
                    responsive
                  />
                )}
              />

              <FieldController
                name="companyName"
                control={control as unknown as Control<FieldValues>}
                rules={new RequiredValidator('Enter your company name')}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="text"
                    label="Company Name"
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your company name',
                    }}
                    responsive
                  />
                )}
              />

              <FieldController
                control={control as unknown as Control<FieldValues>}
                name="password"
                rules={new RequiredValidator('Enter your password')}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <TextField
                      {...field}
                      validation={{
                        dirty: fieldState.isDirty || !!fieldState.error,
                        invalid: !!fieldState.error,
                        message: fieldState.error?.message ?? 'Enter your password',
                      }}
                      label="Password"
                      type="password"
                      responsive
                    />

                    {!!fieldValues?.password && (
                      <div style={{ width: '100%' }}>
                        <PasswordStrength password={fieldValues.password} />
                      </div>
                    )}
                  </Fragment>
                )}
              />

              <FieldController
                control={control as unknown as Control<FieldValues>}
                name="confirmPassword"
                rules={new ConfirmationPasswordValidator('Passwords must match', getValues('password'))}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Confirm your password',
                    }}
                    label="Confirm Password"
                    type="password"
                    responsive
                  />
                )}
              />

              <span className="body" style={{ textAlign: 'start', width: '100%', fontSize: '0.85rem' }}>
                By signing up, you agree to our <a href="https://legal.keplerco.io/documents/terms-of-use/" target="_blank" rel="noopener noreferrer">terms of service</a> and <a href="https://legal.keplerco.io/documents/privacy-policy/" target="_blank" rel="noopener noreferrer">privacy policy</a>
              </span>

              <div style={{ width: '100%', marginTop: '10px' }}>
                <Button fullWidthMobile={true} type="button">
                  Create Free Account
                </Button>
              </div>
            </form>

            {
              isMobile && (
                <div className={classNames(accountStyles.accountNavigationToggler, accountStyles.bottom)}>
                  <span className="body">Already have an account?</span>
                  <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountLogin}`)}>
                    Sign in
                  </TertiaryButton>
                </div>
              )
            }
          </AccountPageLoaderLayer>
        </div></div></div>
  );
}
